import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import DentalProcedurePanel from '../../../components/cdt/DentalProcedurePanel';

const TreatmentTracking = ({ patientId }) => {
  const [treatments, setTreatments] = useState([
    {
      id: 1,
      date: '2026-01-15',
      procedureCode: 'D2391',
      procedureName: 'Resin composite - one surface, posterior',
      tooth: '#14',
      provider: 'Dr. Chen',
      status: 'completed',
      cost: 185.00,
      notes: 'Composite restoration completed without complications'
    },
    {
      id: 2,
      date: '2026-01-20',
      procedureCode: 'D0274',
      procedureName: 'Bitewing radiographs - four films',
      tooth: 'N/A',
      provider: 'Dr. Chen',
      status: 'completed',
      cost: 85.00,
      notes: 'Routine diagnostic imaging'
    },
    {
      id: 3,
      date: '2026-02-05',
      procedureCode: 'D2740',
      procedureName: 'Crown - porcelain fused to metal',
      tooth: '#3',
      provider: 'Dr. Chen',
      status: 'scheduled',
      cost: 1250.00,
      notes: 'Crown prep and temporary placement scheduled'
    }
  ]);

  const [showAddProcedure, setShowAddProcedure] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  const statusOptions = [
    { value: 'all', label: 'All Treatments' },
    { value: 'completed', label: 'Completed' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'pending', label: 'Pending' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-success/10 text-success border-success/20';
      case 'scheduled': return 'bg-primary/10 text-primary border-primary/20';
      case 'in-progress': return 'bg-warning/10 text-warning border-warning/20';
      case 'pending': return 'bg-muted text-muted-foreground border-border';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  const filteredTreatments = filterStatus === 'all' 
    ? treatments 
    : treatments?.filter(t => t?.status === filterStatus);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card rounded-xl border border-border p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-1">Treatment History</h2>
            <p className="text-sm text-muted-foreground">Track completed and planned procedures</p>
          </div>
          <div className="flex items-center gap-3">
            <Select
              options={statusOptions}
              value={filterStatus}
              onChange={setFilterStatus}
              className="w-48"
            />
            <Button 
              variant="default" 
              iconName="Plus" 
              iconPosition="left"
              onClick={() => setShowAddProcedure(!showAddProcedure)}
            >
              Add Procedure
            </Button>
          </div>
        </div>
      </div>

      {/* Add Procedure Panel */}
      {showAddProcedure && (
        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground">Add New Procedure</h3>
            <Button variant="ghost" size="sm" iconName="X" onClick={() => setShowAddProcedure(false)} />
          </div>
          <DentalProcedurePanel 
            onSelect={(procedure) => {
              console.log('Selected procedure:', procedure);
              setShowAddProcedure(false);
            }}
            onAdd={(procedure) => {
              console.log('Add procedure:', procedure);
            }}
            onRemove={(procedureId) => {
              console.log('Remove procedure:', procedureId);
            }}
            onUpdate={(procedure) => {
              console.log('Update procedure:', procedure);
            }}
          />
        </div>
      )}

      {/* Treatment Timeline */}
      <div className="space-y-4">
        {filteredTreatments?.length === 0 ? (
          <div className="bg-card rounded-xl border border-border p-12 text-center">
            <Icon name="ClipboardList" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No treatments found</p>
          </div>
        ) : (
          filteredTreatments?.map((treatment) => (
            <div key={treatment?.id} className="bg-card rounded-xl border border-border p-4 hover:shadow-elevation-2 transition-all">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(treatment?.status)}`}>
                      {treatment?.status?.charAt(0)?.toUpperCase() + treatment?.status?.slice(1)}
                    </span>
                    <span className="text-sm text-muted-foreground">{treatment?.date}</span>
                  </div>
                  
                  <h3 className="font-semibold text-foreground mb-1">
                    {treatment?.procedureName}
                  </h3>
                  
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                    <span className="flex items-center gap-1">
                      <Icon name="Hash" size={14} />
                      {treatment?.procedureCode}
                    </span>
                    {treatment?.tooth !== 'N/A' && (
                      <span className="flex items-center gap-1">
                        <Icon name="Circle" size={14} />
                        Tooth {treatment?.tooth}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Icon name="User" size={14} />
                      {treatment?.provider}
                    </span>
                  </div>
                  
                  {treatment?.notes && (
                    <p className="text-sm text-muted-foreground">{treatment?.notes}</p>
                  )}
                </div>
                
                <div className="text-right">
                  <p className="text-lg font-semibold text-foreground mb-2">
                    ${treatment?.cost?.toFixed(2)}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" iconName="Edit" />
                    <Button variant="ghost" size="sm" iconName="Image" />
                    <Button variant="ghost" size="sm" iconName="MoreVertical" />
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 bg-success/10 rounded-lg">
              <Icon name="CheckCircle" size={24} className="text-success" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">
                {treatments?.filter(t => t?.status === 'completed')?.length}
              </p>
              <p className="text-sm text-muted-foreground">Completed</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
              <Icon name="Calendar" size={24} className="text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">
                {treatments?.filter(t => t?.status === 'scheduled')?.length}
              </p>
              <p className="text-sm text-muted-foreground">Scheduled</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 bg-warning/10 rounded-lg">
              <Icon name="DollarSign" size={24} className="text-warning" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">
                ${treatments?.reduce((sum, t) => sum + t?.cost, 0)?.toFixed(2)}
              </p>
              <p className="text-sm text-muted-foreground">Total Value</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TreatmentTracking;